import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import JourneyQuoteLoadingIllustration from '~/assets/icons/JourneyQuoteLoadingIllustration';

type JourneyLoadingScreenProps = {
  appId: string;
  /** Mensagem principal; muda por contexto («Processando pagamento…», etc.). */
  message: string;
  /** Texto de apoio abaixo da mensagem (usado nos estados de falha). */
  description?: string;
  /** Ação opcional — nos estados de erro, o «Tentar novamente». */
  action?: ReactNode;
  /** `false` nos estados de falha, onde não há nada a carregar. */
  busy?: boolean;
};

/**
 * Ecrã de espera padrão da plataforma (Figma 1680:5757): ilustração animada
 * `undraw_on-the-way` (entregadora de scooter, 18:7, 117px de altura), 48px de respiro e a mensagem em `h3`.
 *
 * É o mesmo visual em toda a jornada — só o texto muda. Também serve os
 * estados de falha, onde recebe uma `action` (ex.: «Tentar novamente») e
 * `busy={false}` para não anunciar carregamento a leitores de ecrã.
 */
export default function JourneyLoadingScreen({
  appId,
  message,
  description,
  action,
  busy = true
}: JourneyLoadingScreenProps) {
  return (
    <Box
      aria-busy={busy || undefined}
      aria-live="polite"
      data-app-id={appId}
      role="status"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '48px',
        maxWidth: 461,
        textAlign: 'center'
      }}
    >
      <Box aria-hidden sx={{ display: 'block', flexShrink: 0, maxWidth: '100%', width: 301 }}>
        <JourneyQuoteLoadingIllustration />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography component="h1" sx={{ fontWeight: 600 }} variant="h3">
          {message}
        </Typography>
        {description ? (
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            {description}
          </Typography>
        ) : null}
        {action ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>{action}</Box> : null}
      </Box>
    </Box>
  );
}
